/**
 * BusinessAiKit.js
 *
 * Small presentational atoms shared by the Happy AI business screens (digest,
 * alerts, goals) and the management dashboard cards: delta chip, KPI card and
 * goal progress bar. Colours come from the management theme tones, brand
 * orange (#F97316) for the primary accent.
 *
 * @module components/management/BusinessAiKit
 */
import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import Icon from './SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {classifyDelta, formatDeltaPct, formatKpiValue, goalBarRatio} from '../../helpers/businessAi';

const DELTA_ICON = {up: 'trending-up', down: 'trending-down', flat: 'trending-neutral', none: 'minus'};

/**
 * Pill showing a signed percentage delta with an arrow.
 * @param {object} props
 * @param {*} props.deltaPct delta in percent (`null` → renders `—`).
 * @param {boolean} [props.invert=false] lower-is-better metric.
 * @param {string} [props.label] optional muted caption after the pill.
 * @returns {JSX.Element} the chip.
 */
export const DeltaChip = ({deltaPct, invert = false, label}) => {
    const m = useManagementTheme();
    const {direction, tone} = classifyDelta(deltaPct, {invert});
    const color = tone === 'neutral' ? m.colors.subText : m.toneColor(tone);
    const text = formatDeltaPct(deltaPct) ?? '—';

    return (
        <View style={styles.deltaWrap}>
            <View style={[styles.deltaPill, {backgroundColor: tone === 'neutral' ? m.colors.surfaceAlt : m.toneTint(tone)}]}>
                <Icon name={DELTA_ICON[direction]} size={13} color={color} />
                <Text style={[styles.deltaText, {color}]}>{text}</Text>
            </View>
            {!!label && <Text style={[styles.deltaLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>}
        </View>
    );
};

/**
 * Digest KPI card: label, headline value and two deltas (previous / usual).
 * @param {object} props
 * @param {{label: string, value: *, previous: *, deltaPct: *, baselineValue: *, baselineDeltaPct: *, unit: ?string}} props.kpi KPI payload.
 * @param {string} props.previousLabel caption of the previous-period delta.
 * @param {string} props.baselineLabel caption of the baseline delta.
 * @param {boolean} [props.invert=false] lower-is-better metric.
 * @returns {JSX.Element} the card.
 */
export const KpiDeltaCard = ({kpi, previousLabel, baselineLabel, invert = false}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.kpiCard, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            <Text style={[styles.kpiLabel, {color: m.colors.subText}]} numberOfLines={1}>{kpi.label}</Text>
            <Text style={[styles.kpiValue, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit>
                {formatKpiValue(kpi.value, kpi.unit)}
            </Text>
            <DeltaChip deltaPct={kpi.deltaPct} invert={invert} label={previousLabel} />
            {kpi.baselineDeltaPct !== undefined && kpi.baselineDeltaPct !== null && (
                <DeltaChip deltaPct={kpi.baselineDeltaPct} invert={invert} label={baselineLabel} />
            )}
        </View>
    );
};

/**
 * Horizontal goal progress bar with an optional projected marker.
 * @param {object} props
 * @param {number} props.pct current progress in percent.
 * @param {number} [props.projectedPct] projected end-of-period progress in percent.
 * @param {boolean} [props.onTrack] colours the bar green/amber when provided.
 * @returns {JSX.Element} the bar.
 */
export const GoalBar = ({pct, projectedPct, onTrack}) => {
    const m = useManagementTheme();
    const fill = onTrack === false ? m.toneColor('warning') : onTrack === true ? m.toneColor('success') : '#F97316';
    const projected = projectedPct === undefined || projectedPct === null ? null : goalBarRatio(projectedPct);

    return (
        <View style={[styles.barTrack, {backgroundColor: m.colors.surfaceAlt}]}>
            {projected !== null && <View style={[styles.barProjected, {width: `${projected * 100}%`, backgroundColor: fill, opacity: 0.25}]} />}
            <View style={[styles.barFill, {width: `${goalBarRatio(pct) * 100}%`, backgroundColor: fill}]} />
        </View>
    );
};

const styles = StyleSheet.create({
    deltaWrap: {flexDirection: 'row', alignItems: 'center', marginTop: 6},
    deltaPill: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3, gap: 4},
    deltaText: {fontSize: 12, fontWeight: '700'},
    deltaLabel: {fontSize: 11.5, fontWeight: '500', marginLeft: 8, flexShrink: 1},
    kpiCard: {width: '48%', borderWidth: 1, borderRadius: 18, padding: 14},
    kpiLabel: {fontSize: 12.5, fontWeight: '600'},
    kpiValue: {fontSize: 22, fontWeight: '800', marginTop: 4},
    barTrack: {height: 10, borderRadius: 999, overflow: 'hidden', justifyContent: 'center'},
    barProjected: {position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 999},
    barFill: {position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 999},
});
